/**
 * One place for the facts that appear in more than one component.
 *
 * The GEO audit of 2026-09-04 found four different prices on the site for what looked
 * like one offer ($20, $50, $99, plus a "$50 / Lifetime $129" banner and a "$7 a month"
 * meta description that was really the Skool price). Anything a crawler or a model can
 * quote as a number now comes from here, so it can only be wrong in one place.
 */

export const SITE = {
  name: "AI Automation Base",
  /** Short name. Never "AI AutoBase" — one word is a different brand in Google's index. */
  shortName: "AI Auto Base",
  origin: "https://aiautobase.com",
  email: "info@aiautobase.com",
  telephone: "+1-610-672-4924",
  region: "PA",
  country: "US",
  founder: "Hector Diaz",
  parent: { name: "Orbix Automation Solutions", url: "https://getorbix.com/" },
  /**
   * Every one of these was fetched and confirmed to return real profile content on
   * 2026-09-04 — not a login wall, which answers 200 and would otherwise look fine.
   * A sameAs pointing at a profile that does not exist is worse than a short list:
   * it is the exact signal a knowledge graph uses to decide two entities are one.
   * Mirrored in the workspace .env as AIAB_*_URL. Keep the two in step.
   */
  sameAs: [
    "https://x.com/aiautobase",
    "https://www.youtube.com/@AIAutoBase",
    "https://www.instagram.com/aiautobaseteam/",
    "https://www.tiktok.com/@aiautobase",
    "https://www.facebook.com/profile.php?id=61588798776959",
    "https://www.linkedin.com/company/ai-automation-base/",
    "https://www.skool.com/aiautomation-by-adam-8725",
    "https://www.skool.com/ai-automations-by-hector-8106",
  ],
} as const;

export type Plan = {
  key: "monthly" | "yearly" | "lifetime";
  name: string;
  /** Numeric price in USD. The display string is derived, never typed twice. */
  amount: number;
  period: string;
  /** subscription plans carry a billing period for schema.org; lifetime does not. */
  billingDuration: "P1M" | "P1Y" | null;
  description: string;
  features: string[];
  popular: boolean;
};

export const PLANS: Plan[] = [
  {
    key: "monthly",
    name: "Monthly",
    amount: 20,
    period: " / month",
    billingDuration: "P1M",
    description: "Full access, billed monthly",
    features: [
      "Courses on Claude Code, n8n and AntiGravity",
      "AI Automation & Agency Roadmaps",
      "Claude Skills library",
      "Self-hosted n8n workflow library",
      "Templates with step-by-step video guides",
      "Resources Vault access",
      "Email & live chat support",
    ],
    popular: false,
  },
  {
    key: "yearly",
    name: "Yearly",
    amount: 50,
    period: " / year",
    billingDuration: "P1Y",
    description: "Everything, billed annually — best value",
    features: [
      "Everything in Monthly",
      "Complete roadmap library",
      "Marketing automations with setup video guides",
      "Build Free AI Agency Website guide",
      "Premium Resources Vault access",
      "Launch Your AI Agency",
      "Priority support",
    ],
    popular: true,
  },
  {
    key: "lifetime",
    name: "Lifetime",
    amount: 99,
    period: " one-time",
    billingDuration: null,
    description: "Pay once, keep it forever",
    features: [
      "Everything in Yearly",
      "Lifetime updates & access",
      "1-on-1 consultation",
      "Full AI automation course library",
      "All future courses & templates",
      "Priority support",
    ],
    popular: false,
  },
];

export const priceLabel = (p: Plan) => `$${p.amount}`;

/** The cheapest recurring way in. Used by the hero, the sticky bar and the meta description. */
export const ENTRY_PLAN = PLANS[0];
export const ENTRY_PRICE_TEXT = `From $${ENTRY_PLAN.amount} a month`;

/**
 * Counts shown on the homepage. Every one is checkable: the first two come from the
 * same tables the pages read, the third from the public Skool about page. No client
 * counts, no revenue claims, no invented percentages.
 */
export const VERIFIED_AT = "2026-09-04";
